import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';
import { SortComponent } from '../../core/components/sort/sort.component';
import { PaginationComponent } from '../../core/components/pagination/pagination.component';
import { SearchComponent } from '../../core/components/search/search.component';
import { Client, PageInfo, PermissionsEnum, SchedulerStatus, SortingType, UtilityCountry, UtilityDto, UtilityGetAllDataIn, UtilityStatus, UtilityStatusDataIn } from '../../../api/api-reference';
import { AuthService } from '../../../services/auth.service';
import { UtilityDetailsComponent } from '../utility-details/utility-details.component';
import { DateRangeInput } from '../../core/models/date-range-input';
import { DatepickerComponent } from '../../core/components/datepicker/datepicker.component';
import { StatusComponent } from '../../core/components/status/status.component';
import { CountryFilterComponent } from '../../core/components/country-filter/country-filter.component';
import { processDateRange } from '../../../helper/date-utils';

@Component({
  selector: 'app-utility-management',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AngularSvgIconModule,
    SearchComponent,
    PaginationComponent,
    SortComponent,
    UtilityDetailsComponent,
    DatepickerComponent,
    StatusComponent,
    CountryFilterComponent
  ],
  templateUrl: './utility-management.component.html',
  styleUrl: './utility-management.component.scss'
})
export class UtilityManagementComponent implements OnInit {
  public pageSize: number = 5;
  public pageNumber: number = 1;
  public currentPage: number = 1;
  public numberOfReminders: number = 0;
  public lastPageSize: number = 1;
  selectedRowId: string | null = null;
  activeRow: number | null = null;
  reminderData: UtilityDto[] | undefined = [];
  isModalOpen = false;
  isDatepickerOpen = false;
  requestData = new UtilityGetAllDataIn({
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
    search: undefined,
    sorting: undefined,
    startDate: undefined,
    endDate: undefined,
    status: undefined
  })
  UtilityStatus = UtilityStatus;
  PermissionsEnum = PermissionsEnum;

  public selectedUtility: UtilityDto | undefined;

  public colorMap: { [key: string]: string } = {};

  utilityStatuses = Object.values(UtilityStatus).map(s => s.toUpperCase());
  utilityCountries = Object.values(UtilityCountry).map(s => s.toString());

  constructor(
    private toastr: ToastrService,
    public router: Router,
    public client: Client,
    public authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.reloadTable(1);
  }

  onSortOption(selectedSort?: SortingType) {
    if (selectedSort) this.requestData.sorting = selectedSort;
    this.reloadTable();
  }

  searchChange(searchText: string) {
    this.requestData.search = searchText;
    this.reloadTable(1);
  }

  reloadTable(page?: number) {
    this.pageNumber = page || 0;
    if (page) this.requestData!.pageInfo!.page = this.pageNumber;

    this.client.utilityQuery(this.requestData).subscribe({
      next: (response) => {
        this.reminderData = response.data;
        this.numberOfReminders = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfReminders / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  pageChange(value: number) {
    this.currentPage = value;
    this.reloadTable(this.currentPage);
  }

  setActiveRow(rowIndex: number | null) {
    this.activeRow = this.activeRow === rowIndex ? null : rowIndex;
  }

  handleRowClickUtility(row: UtilityDto) {
      if (!this.isModalOpen) this.selectedUtility = row;
      else this.selectedRowId = row.id!;
    }

  editUtility(id: string) {
    this.router.navigate(['/utilities/utility-setup'], {
      state: { utilityData: id },
    });
  }

  updateStatus(id: string, status: UtilityStatus) {
    const utilityStatus = new UtilityStatusDataIn({
      utilityId: id,
      status: status
    });
    
    this.client.updateUtilityStatus(utilityStatus).subscribe({
      next: (response) => {
        this.toastr.success(response);
        this.reloadTable();
        this.setActiveRow(null);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  selectedDate: string | null = '';
  onDateRangeChanged(input: DateRangeInput): void {
   const result = processDateRange(input);

    this.requestData.startDate = result.startDate;
    this.requestData.endDate = result.endDate;
    this.selectedDate = result.selectedDate;
    this.reloadTable(1);
  }

  clearDates(): void {
    this.requestData.startDate = undefined;
    this.requestData.endDate = undefined;
    this.reloadTable();
  }

  toggleModal() {
    this.isDatepickerOpen = !this.isDatepickerOpen;
  }

  onStatusOption(selectedStatus?: string) {
    if (selectedStatus !== undefined) {
      this.requestData.status =
        selectedStatus === 'ALL'
          ? undefined
          : (selectedStatus as UtilityStatus);
    }
    this.reloadTable(1);
  }

  onCountryOption(selectedCountry?: string) {
    if (selectedCountry !== undefined) {
      this.requestData.country =
        selectedCountry === 'All'
          ? undefined
          : (selectedCountry as UtilityCountry);
    }
    this.reloadTable(1);
  }
}
